// src/utils/itinerary.js
// Helper to generate an itinerary with different activities per day when possible.

function shuffle(array) {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * generateItinerary
 * @param {Array} activities - array of activity objects (must have id and title)
 * @param {number} days
 * @param {Object} options - { random: boolean, uniqueOnly: boolean }
 * @returns {Array} array of activities length == days (items or null for free time)
 *
 * Strategy:
 * - If days <= activities.length: take the first N (or a shuffled sample if random=true) — all distinct.
 * - If days > activities.length: produce a sequence that cycles through activities but reshuffles between cycles
 *   to avoid repeating the same activity twice in a row when possible.
 */
export function generateItinerary(activities = [], days = 7, options = { random: false, uniqueOnly: false }) {
  const n = Math.max(0, Number(days || 0));
  if (!activities || activities.length === 0) return Array.from({ length: n }, () => null);

  const poolBase = options.random ? shuffle(activities) : activities.slice();
  // If uniqueOnly is requested and days exceed available activities, fill remaining days with null (free time)
  if (options.uniqueOnly) {
    if (n <= poolBase.length) return poolBase.slice(0, n);
    const result = poolBase.slice();
    // pad with nulls for remaining days (no duplicates)
    return result.concat(Array.from({ length: n - result.length }, () => null));
  }

  // Default behavior: try to avoid consecutive repeats, allow cycling when days > activities.length
  if (n <= poolBase.length) {
    return poolBase.slice(0, n);
  }

  const result = [];
  let pool = poolBase.slice();
  while (result.length < n) {
    for (let i = 0; i < pool.length && result.length < n; i++) {
      const candidate = pool[i];
      const last = result[result.length - 1];
      if (last && last.id === candidate.id) {
        // try to swap with next to avoid immediate repeat
        if (i + 1 < pool.length) {
          const alt = pool[i + 1];
          result.push(alt);
          // leave candidate to be used later
          continue;
        }
        // if no alternative, allow repeat (single-activity case)
      }
      result.push(candidate);
    }

    // if still need more, reshuffle pool to create a varied next cycle
    pool = options.random ? shuffle(poolBase) : poolBase.slice();
    // small safeguard: if pool is single item and last equals same, we'll end up repeating
    if (pool.length === 1 && result.length < n) {
      while (result.length < n) result.push(pool[0]);
    }
  }

  return result.slice(0, n);
}

export default generateItinerary;
